import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { HelpDocsService, readableBytes } from '@mode/shared/util-js';

@Component({
  selector: 'mode-flamingo-error-ng',
  templateUrl: './flamingo-error-ng.component.html',
  styleUrls: ['./flamingo-error-ng.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlamingoErrorNgComponent implements OnInit, OnChanges {
  // bindings
  @Input() errorResponse!: FlamingoResponse.FlamingoErrorResponse;
  @Input() csvLink: string | undefined;

  // outputs
  @Output() notebookSelected: EventEmitter<null> = new EventEmitter();

  constructor(public helpDocs: HelpDocsService) {}

  // local
  isUnknownError = true;
  errorType!: string;
  columns: FlamingoResponse.ColumnErrorResult[] = [];
  context!: { [key: string]: any };
  dataProblems: FlamingoResponse.DataProblemResult[] = [];

  ngOnInit(): void {
    this.initLocalAttributes();
    const response = this.errorResponse;
    if (response != null && 'result' in response && 'stateProperties' in response.result) {
      const failureCode =
        'failureCode' in response.result.stateProperties
          ? response.result.stateProperties.failureCode
          : response.result.stateProperties.code;

      this.errorType =
        failureCode in FlamingoResponse.DatasetErrors ? failureCode : FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
      this.isUnknownError = this.errorType === FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
      if (!this.extractErrorSpecificAttributes(this.errorResponse)) {
        this.context = response.result.stateProperties;
      }
    } else if (response && response.type === FlamingoResponse.ResponseType.SizeLimit) {
      this.isUnknownError = false;
      this.errorType = FlamingoResponse.ResponseType.SizeLimit;
    } else if (this.isServiceErrorResponse(response)) {
      const failureCode = response.payload.code;
      this.errorType =
        (FlamingoResponse.DatasetErrors as any)[failureCode] || FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
      this.isUnknownError = this.errorType === FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
      if (!this.extractErrorSpecificAttributes(this.errorResponse)) {
        this.context = response.payload.context;
      }
    } else {
      this.isUnknownError = true;
    }
  }

  private initLocalAttributes() {
    this.columns = [];
    this.dataProblems = [];
    this.context = {};
  }

  private isServiceErrorResponse(response: any): response is FlamingoResponse.ServiceErrorResponse {
    return response != null && 'payload' in response && 'code' in response.payload;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // On changes, we update `columns` and `dataProblems` when relevant to make the template more straightforward
    if (changes.errorResponse && changes.errorResponse.currentValue) {
      this.extractErrorSpecificAttributes(changes.errorResponse.currentValue);
    }
  }

  private extractErrorSpecificAttributes(error: FlamingoResponse.FlamingoErrorResponse): boolean {
    if (error.type === FlamingoResponse.ResponseType.DatasetFailed) {
      if ('columns' in error.result.stateProperties) {
        this.initLocalAttributes();
        this.columns = error.result.stateProperties.columns;
        return true;
      } else if ('dataProblems' in error.result.stateProperties) {
        this.initLocalAttributes();
        this.dataProblems = error.result.stateProperties.dataProblems;
        return true;
      }
    } else if (this.isServiceErrorResponse(error) && 'dataProblems' in error.payload.context) {
      this.initLocalAttributes();
      this.dataProblems = error.payload.context.dataProblems;
      return true;
    }
    return false;
  }

  goToNotebook() {
    this.notebookSelected.emit();
  }

  readableBytes(size: number): string {
    return readableBytes(size);
  }

  get formattedByteLimit(): string {
    if (this.errorResponse.type === FlamingoResponse.ResponseType.SizeLimit) {
      return readableBytes(this.errorResponse.limit as number);
    }
    return '';
  }

  get formattedByteSize(): string {
    if (this.errorResponse.type === FlamingoResponse.ResponseType.SizeLimit) {
      return readableBytes(this.errorResponse.actual);
    }
    return '';
  }

  get formattedOverageSize(): string {
    if (this.errorResponse.type === FlamingoResponse.ResponseType.SizeLimit) {
      return readableBytes(this.errorResponse.actual - (this.errorResponse.limit as number));
    }
    return '';
  }
}
